import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { submissions, products, documents } from '../services/api.js';
import { DocumentUpload } from '../components/DocumentUpload.jsx';
import './BrokerPortal.css';

const STATUS_STEPS = ['Submitted', 'Under Review', 'Quoted', 'Bound'];

function getStepIndex(status) {
  if (!status) return 0;
  const normalized = (status || '').toLowerCase().replace(/\s+/g, '_');
  if (normalized === 'new' || normalized === 'submitted') return 0;
  if (normalized === 'in_review' || normalized === 'assigned') return 1;
  if (normalized === 'quoted') return 2;
  if (normalized === 'bound') return 3;
  if (normalized === 'declined') return -1;
  return 0;
}

function getStatusColor(status) {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'declined') return 'declined';
  if (normalized === 'bound') return 'bound';
  if (normalized === 'quoted') return 'quoted';
  if (normalized === 'in_review' || normalized === 'assigned') return 'review';
  return 'submitted';
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function BrokerPortal() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [productList, setProductList] = useState([]);

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [insuredName, setInsuredName] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [createdSubmission, setCreatedSubmission] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    products
      .list()
      .then((res) => {
        const items = res.data || res;
        setProductList(Array.isArray(items) ? items : []);
      })
      .catch(() => {});
  }, []);

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await submissions.list({
        broker_id: user?.tenantId || user?.userId,
      });
      const items = result.data || result;
      setData(Array.isArray(items) ? items : []);
    } catch (err) {
      setError(err.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [user?.tenantId, user?.userId]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const resetWizard = () => {
    setWizardStep(1);
    setInsuredName('');
    setSelectedProduct('');
    setSelectedFiles([]);
    setUploadError(null);
    setUploadSuccess(false);
    setWizardOpen(false);
  };

  const handleSubmit = async () => {
    if (!insuredName || !selectedProduct) return;
    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);
    try {
      const result = await submissions.create({
        productId: selectedProduct,
        insuredName,
        source: 'upload',
        brokerID: user?.userId || '',
      });
      const sub = result.data || result;
      const subId = sub.submissionId || sub.SubmissionID;
      const acctId = sub.accountId || sub.AccountID;

      if (selectedFiles.length > 0 && subId && acctId) {
        for (const file of selectedFiles) {
          try {
            const uploadRes = await documents.upload({
              accountId: acctId,
              submissionId: subId,
              fileName: file.name,
              fileType: file.type || 'application/octet-stream',
              fileSize: file.size,
              docType: 'application',
            });
            const data = uploadRes.data || uploadRes;
            await fetch(data.uploadUrl, {
              method: 'PUT',
              body: file,
              headers: { 'Content-Type': file.type || 'application/octet-stream' },
            });
            await documents.process(data.documentId);
          } catch (fileErr) {
            console.error('File upload failed:', file.name, fileErr);
          }
        }
      }

      setUploadSuccess(true);
      if (subId && acctId) {
        setCreatedSubmission({ submissionId: subId, accountId: acctId, insuredName });
      }
      loadSubmissions();
      setTimeout(() => {
        setUploadSuccess(false);
        resetWizard();
      }, 2000);
    } catch (err) {
      setUploadError(err.message || 'Submission failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = (files) => {
    if (!files || files.length === 0) return;
    setSelectedFiles(Array.from(files));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleFileSelect = (e) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCardClick = (sub) => {
    const status = (sub.Status || '').toLowerCase();
    const subId = sub.SubmissionID || sub.submissionId;
    if ((status === 'quoted' || status === 'bound') && subId) {
      navigate(`/broker/quotes/${subId}`);
    }
  };

  const canProceedStep1 = insuredName.trim() && selectedProduct;
  const canSubmit = canProceedStep1;

  return (
    <div className="bp">
      <header className="bp__header">
        <h1 className="bp__title">Broker Portal</h1>
        <button
          className="bp__cta btn btn--primary"
          onClick={() => setWizardOpen(true)}
          aria-label="Start new submission"
        >
          <span className="bp__cta-icon" aria-hidden>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </span>
          Start New Submission
        </button>
      </header>

      {/* Wizard modal */}
      {wizardOpen && (
        <div className="bp__wizard-overlay" onClick={() => !uploading && resetWizard()}>
          <div className="bp__wizard card" onClick={(e) => e.stopPropagation()}>
            <div className="bp__wizard-header">
              <h2 className="bp__wizard-title">New Submission</h2>
              <button
                className="bp__wizard-close"
                onClick={resetWizard}
                disabled={uploading}
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="bp__wizard-steps">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={`bp__wizard-step-indicator ${wizardStep >= step ? 'bp__wizard-step-indicator--active' : ''}`}
                >
                  <span className="bp__wizard-step-num">{step}</span>
                  <span className="bp__wizard-step-label">
                    {step === 1 ? 'Basic Info' : step === 2 ? 'Documents' : 'Review'}
                  </span>
                  {step < 3 && <span className="bp__wizard-step-connector" />}
                </div>
              ))}
            </div>

            <div className="bp__wizard-body">
              {wizardStep === 1 && (
                <div className="bp__wizard-panel">
                  <div className="bp__form-group">
                    <label className="bp__form-label">Insured Name</label>
                    <input
                      type="text"
                      className="bp__form-input"
                      value={insuredName}
                      onChange={(e) => setInsuredName(e.target.value)}
                      placeholder="Enter insured company name"
                      autoFocus
                    />
                  </div>
                  <div className="bp__form-group">
                    <label className="bp__form-label">Product</label>
                    <select
                      className="bp__form-select"
                      value={selectedProduct}
                      onChange={(e) => setSelectedProduct(e.target.value)}
                    >
                      <option value="">Select a product...</option>
                      {productList.map((p) => (
                        <option key={p.ProductID || p.ID} value={p.ProductID || p.ID}>
                          {p.Name || p.ProductName || p.ProductID}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="bp__wizard-actions">
                    <button className="btn btn--secondary" onClick={resetWizard}>
                      Cancel
                    </button>
                    <button
                      className="btn btn--primary"
                      onClick={() => setWizardStep(2)}
                      disabled={!canProceedStep1}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}

              {wizardStep === 2 && (
                <div className="bp__wizard-panel">
                  <div
                    className={`bp__upload ${dragOver ? 'bp__upload--active' : ''}`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="bp__upload-icon">
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                        <path d="M24 6v26m0-26l-8 8m8-8l8 8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M8 32v6a4 4 0 004 4h24a4 4 0 004-4v-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <p className="bp__upload-text">
                      {selectedFiles.length > 0
                        ? `${selectedFiles.length} file(s) selected`
                        : 'Drag & drop files here or click to browse'}
                    </p>
                    <p className="bp__upload-hint">PDF, Excel, Word, CSV — optional</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="bp__file-input"
                      onChange={handleFileSelect}
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.zip"
                    />
                  </div>
                  {selectedFiles.length > 0 && (
                    <ul className="bp__file-chips">
                      {selectedFiles.map((f, i) => (
                        <li key={`${f.name}-${i}`} className="bp__file-chip">
                          <span>{f.name}</span>
                          <button type="button" onClick={(e) => { e.stopPropagation(); removeFile(i); }} aria-label={`Remove ${f.name}`}>×</button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="bp__wizard-actions">
                    <button className="btn btn--secondary" onClick={() => setWizardStep(1)}>
                      ← Back
                    </button>
                    <button
                      className="btn btn--primary"
                      onClick={() => setWizardStep(3)}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}

              {wizardStep === 3 && (
                <div className="bp__wizard-panel">
                  <div className="bp__review">
                    <div className="bp__review-row">
                      <span className="bp__review-label">Insured</span>
                      <span className="bp__review-value">{insuredName}</span>
                    </div>
                    <div className="bp__review-row">
                      <span className="bp__review-label">Product</span>
                      <span className="bp__review-value">
                        {productList.find((p) => (p.ProductID || p.ID) === selectedProduct)?.Name || productList.find((p) => (p.ProductID || p.ID) === selectedProduct)?.ProductName || selectedProduct}
                      </span>
                    </div>
                    <div className="bp__review-row">
                      <span className="bp__review-label">Documents</span>
                      <span className="bp__review-value">{selectedFiles.length} file(s)</span>
                    </div>
                  </div>
                  {uploadSuccess && (
                    <p className="bp__upload-success">Submission created successfully!</p>
                  )}
                  {uploadError && <p className="bp__upload-error">{uploadError}</p>}
                  <div className="bp__wizard-actions">
                    <button className="btn btn--secondary" onClick={() => setWizardStep(2)} disabled={uploading}>
                      ← Back
                    </button>
                    <button
                      className="btn btn--primary"
                      onClick={handleSubmit}
                      disabled={uploading || !canSubmit}
                    >
                      {uploading ? 'Submitting...' : 'Submit Application'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Document upload after submission */}
      {createdSubmission && (
        <div className="bp__upload-section card">
          <h2 className="bp__section-title">Upload Documents for {createdSubmission.insuredName}</h2>
          <p className="bp__upload-desc">
            Upload additional application documents to automatically extract and process submission data.
          </p>
          <DocumentUpload
            submissionId={createdSubmission.submissionId}
            accountId={createdSubmission.accountId}
            onComplete={() => {
              setCreatedSubmission(null);
              loadSubmissions();
            }}
          />
          <button
            className="btn btn--secondary bp__skip-btn"
            onClick={() => {
              setCreatedSubmission(null);
              loadSubmissions();
            }}
          >
            Skip — Upload Later
          </button>
        </div>
      )}

      {/* Submissions grid */}
      <section className="bp__list-section">
        <h2 className="bp__section-title">Your Submissions</h2>

        {error && (
          <div className="bp__error">
            <p>{error}</p>
            <button className="btn btn--secondary" onClick={loadSubmissions}>Retry</button>
          </div>
        )}

        {loading ? (
          <div className="bp__loading">
            <div className="spinner" />
            <p>Loading submissions...</p>
          </div>
        ) : Array.isArray(data) && data.length > 0 ? (
          <div className="bp__submissions">
            {data.map((sub) => {
              const status = sub.Status || 'new';
              const step = getStepIndex(status);
              const statusColor = getStatusColor(status);
              const isDeclined = status === 'declined';
              const isClickable = (status || '').toLowerCase() === 'quoted' || (status || '').toLowerCase() === 'bound';

              return (
                <article
                  key={sub.SubmissionID}
                  className={`bp__card card bp__card--${statusColor} ${isClickable ? 'bp__card--clickable' : ''}`}
                  onClick={() => handleCardClick(sub)}
                >
                  <div className="bp__card-accent" />
                  <div className="bp__card-stepper">
                    {STATUS_STEPS.map((label, i) => (
                      <div
                        key={label}
                        className={`bp__stepper-item ${!isDeclined && i <= step ? 'bp__stepper-item--done' : ''} ${i === step && !isDeclined ? 'bp__stepper-item--current' : ''}`}
                      >
                        <span className="bp__stepper-dot" />
                        <span className="bp__stepper-label">{label}</span>
                        {i < STATUS_STEPS.length - 1 && <span className="bp__stepper-line" />}
                      </div>
                    ))}
                  </div>
                  <div className="bp__card-body">
                    <div className="bp__card-header">
                      <h3 className="bp__card-insured">{sub.InsuredName || sub.AccountID || sub.SubmissionID || 'Submission'}</h3>
                      <span className={`bp__card-badge bp__card-badge--${statusColor}`}>
                        {status.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="bp__card-meta">
                      <span>{sub.ProductName || sub.ProductID || '—'}</span>
                      <span className="bp__card-date">{formatDate(sub.CreatedAt)}</span>
                    </div>
                    {isClickable && (
                      <p className="bp__card-hint">Click to view quote</p>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="bp__empty card">
            <div className="bp__empty-icon">
              <svg width="64" height="64" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 8v32M36 8v32M8 12h32M8 36h32" />
              </svg>
            </div>
            <p>No submissions yet.</p>
            <button className="btn btn--primary" onClick={() => setWizardOpen(true)}>
              Start your first submission
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
